import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Badge } from './ui';
import { enrollmentService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';

/** Detail view for one pending enrollment request - what used to be a
 * flat row with just Approve/Reject buttons. Shows everything the
 * approver needs to actually make a decision (requested plan, note,
 * proof-of-payment attachment) and lets them grant a one-off price for
 * this specific student (e.g. a promotional rate) right at approval time
 * - that becomes the enrollment's agreed_price, which is what "potential
 * revenue" is tracked from until an actual Payment is recorded. */
export default function PendingEnrollmentModal({ enrollment, onClose, onApproved, onRejected }) {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [price, setPrice] = useState('');
  const [priceNote, setPriceNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const defaultPrice = enrollment?.discounted_price ?? enrollment?.module_price ?? 0;

  useEffect(() => {
    if (!enrollment) return;
    setPrice(String(defaultPrice));
    setPriceNote('');
    setRejecting(false);
    setRejectReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollment?.id]);

  if (!enrollment) return null;

  const isOverridden = Number(price) !== Number(defaultPrice);
  const currency = enrollment.module_currency || 'DZD';

  const handleApprove = async () => {
    const parsed = Number(price);
    if (Number.isNaN(parsed) || parsed < 0) {
      addNotification(t('Enter a valid price'), 'error');
      return;
    }
    setSubmitting(true);
    try {
      await enrollmentService.approveEnrollment(enrollment.id, {
        price: parsed,
        price_note: isOverridden ? priceNote : '',
      });
      addNotification(t('Enrollment approved'), 'success');
      onApproved?.();
    } catch (err) {
      addNotification(err.response?.data?.price?.[0] || t('Failed to approve enrollment'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await enrollmentService.rejectEnrollment(enrollment.id, rejectReason);
      addNotification(t('Enrollment rejected'), 'success');
      onRejected?.();
    } catch (err) {
      addNotification(t('Failed to reject enrollment'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={!!enrollment} onClose={onClose} title={t('Enrollment Request')} size="md">
      <div className="p-6 space-y-5">
        <div>
          <p className="font-semibold text-gray-900">
            {enrollment.student?.first_name} {enrollment.student?.last_name}
          </p>
          <p className="text-sm text-gray-500">{enrollment.student?.email}</p>
          <p className="text-xs text-gray-400 mt-1">
            {t('Requested')}: {new Date(enrollment.created_at).toLocaleString()}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">{t('Course')}</p>
            <p className="font-medium text-gray-800">{enrollment.module_name}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">{t('Plan')}</p>
            <p className="font-medium text-gray-800">{enrollment.subscription_name}</p>
          </div>
        </div>

        {enrollment.coupon && (
          <div className="flex items-center gap-2 text-sm">
            <Badge tone="purple">{enrollment.coupon.code}</Badge>
            <span className="text-gray-500">
              {t('List price')} {enrollment.module_price} {currency} {t('→')} {enrollment.discounted_price} {currency}
            </span>
          </div>
        )}

        {enrollment.student_note && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">{t('Note from student')}</p>
            <p className="text-sm text-gray-700 bg-amber-50 border border-amber-100 rounded-lg p-3">{enrollment.student_note}</p>
          </div>
        )}

        {enrollment.attachment && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">{t('Proof of payment')}</p>
            <a
              href={enrollment.attachment}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:underline"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('View attachment')}
            </a>
          </div>
        )}

        {!rejecting ? (
          <div className="border-t border-gray-100 pt-4">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              {t('Price for this student')} ({currency})
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none"
            />
            {isOverridden && (
              <input
                type="text"
                value={priceNote}
                onChange={(e) => setPriceNote(e.target.value)}
                placeholder={t('Reason (e.g. Special promotion for this student)')}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mt-2 focus:border-primary-400 focus:outline-none"
              />
            )}
            <p className="text-xs text-gray-400 mt-1">
              {t('Defaults to the list price. Changing it applies only to this one enrollment.')}
            </p>
          </div>
        ) : (
          <div className="border-t border-gray-100 pt-4">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('Rejection reason (optional)')}</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none resize-none"
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          {rejecting ? (
            <>
              <Button variant="secondary" onClick={() => setRejecting(false)} disabled={submitting}>{t('Back')}</Button>
              <Button variant="danger" onClick={handleReject} loading={submitting}>{t('Confirm Rejection')}</Button>
            </>
          ) : (
            <>
              <Button variant="danger" onClick={() => setRejecting(true)} disabled={submitting}>{t('Reject')}</Button>
              <Button variant="success" onClick={handleApprove} loading={submitting}>{t('Approve')}</Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
